import { memo, useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getApiRequest } from "../../../services/getApiRequest";
import { postApiRequest } from "../../../services/postApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import "./AchievementNotificationModal.scss";

const formatTierLabel = (tier) => {
  if (!tier) return "";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};

const normalizeTierKey = (tier) => (tier || "").toLowerCase();

const AchievementNotificationModal = () => {
  const { t } = useTranslation("common");
  const isLogin = useSelector((state) => state.login.isLogin);
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [acknowledging, setAcknowledging] = useState(false);

  const fetchPendingNotifications = useCallback(async () => {
    try {
      const response = await getApiRequest(END_POINTS.GET_ACHIEVEMENT_NOTIFICATIONS_PENDING);
      if (response?.success) {
        setNotifications(response.notifications || []);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error("Error fetching achievement notifications:", error);
    }
  }, []);

  useEffect(() => {
    if (isLogin) {
      fetchPendingNotifications();
    } else {
      setNotifications([]);
      setCurrentIndex(0);
    }
  }, [isLogin, fetchPendingNotifications]);

  const currentNotification = notifications[currentIndex];
  const isOpen = notifications.length > 0 && !!currentNotification;

  const acknowledgeCurrent = useCallback(async () => {
    if (!currentNotification?._id) return;

    setAcknowledging(true);
    try {
      await postApiRequest(END_POINTS.ACK_ACHIEVEMENT_NOTIFICATIONS, {
        achievementIds: [currentNotification._id],
      });
    } catch (error) {
      console.error("Error acknowledging achievement notification:", error);
    } finally {
      setAcknowledging(false);
    }
  }, [currentNotification?._id]);

  const handleOk = useCallback(async () => {
    await acknowledgeCurrent();

    if (currentIndex < notifications.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    setNotifications([]);
    setCurrentIndex(0);
  }, [currentIndex, acknowledgeCurrent, notifications.length]);

  if (!isOpen) return null;

  const tierLabel = formatTierLabel(currentNotification.tier);
  const tierKey = normalizeTierKey(currentNotification.tier);

  return (
    <Modal
      open={isOpen}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      maskClosable={false}
      width={400}
      destroyOnClose
      className="achievement-notification-modal"
    >
      <div className={`achievement-notification-content achievement-notification-content--${tierKey}`}>
        <div className="achievement-notification-icon-wrap">
          <img
            src={currentNotification.icon}
            alt={currentNotification.name}
            className="achievement-notification-icon"
            loading="lazy"
          />
        </div>

        <div className="achievement-notification-tier">
          {/* <span className="achievement-notification-tier-label">{tierLabel}</span> */}
          <div className="achievement-notification-tier-bar">
            <span />
          </div>
        </div>

        <p className="achievement-notification-message">
          <Trans
            ns="common"
            i18nKey="achievements.congratulations_unlocked"
            values={{ tier: tierLabel, name: currentNotification.name }}
            components={[
              <span
                key="tier"
                className={`achievement-notification-tier-text achievement-notification-tier-text--${tierKey}`}
              />,
              <strong key="name" className="achievement-notification-name" />,
            ]}
          />
        </p>

        {notifications.length > 1 && (
          <p className="achievement-notification-counter">
            {t("achievements.notification_counter", {
              current: currentIndex + 1,
              total: notifications.length,
              defaultValue: `${currentIndex + 1} / ${notifications.length}`,
            })}
          </p>
        )}

        <button
          type="button"
          className="achievement-notification-ok-btn"
          onClick={handleOk}
          disabled={acknowledging}
        >
          {t("achievements.great", "Great!")}
        </button>
      </div>
    </Modal>
  );
};

export default memo(AchievementNotificationModal);
